import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Sparkles, TrendingUp } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/8360497/pexels-photo-8360497.jpeg?auto=compress&cs=tinysrgb&w=1200";

const SALES = [
  { item: "Glow-Up Guide (PDF)", amt: "+$29.00" },
  { item: "1:1 Strategy Call", amt: "+$149.00" },
  { item: "Creator Course", amt: "+$199.00" },
];

const PHONE_PRODUCTS = [
  ["Glow-Up Guide", "$29"],
  ["Creator Course", "$199"],
  ["1:1 Strategy Call", "$149"],
];

const STATS = [
  ["5 min", "Average setup"],
  ["$0", "Platform fees"],
  ["24/7", "Selling for you"],
  ["3.1x", "Avg. revenue lift"],
];

function SaleToast() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % SALES.length), 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 16, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.35 }}
        className="flex items-center gap-3 bg-white rounded-2xl shadow-[0_16px_40px_rgba(10,10,10,0.14)] border border-neutral-100 px-4 py-3"
        data-testid="hero-sale-toast"
      >
        <span className="mz-gold-fill w-8 h-8 rounded-full flex items-center justify-center shrink-0">
          <BadgeCheck size={15} className="text-[#1a1206]" strokeWidth={2.2} />
        </span>
        <div>
          <p className="text-[11px] font-semibold text-neutral-800 leading-tight">New sale · {SALES[i].item}</p>
          <p className="text-[11px] font-bold mz-gold-text leading-tight">{SALES[i].amt} just hit your Stripe</p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function PhoneMockup() {
  return (
    <div className="bg-white rounded-[1.75rem] border border-neutral-200 shadow-[0_30px_70px_rgba(10,10,10,0.16)] p-4 w-full" data-testid="hero-phone-mockup">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="mz-gold-fill mz-h w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold text-[#1a1206]">
          MC
        </span>
        <div>
          <p className="mz-h text-[13px] font-semibold leading-tight">@mia.creates</p>
          <p className="text-[10px] text-neutral-400 leading-tight">Creator · 48.2k followers</p>
        </div>
      </div>
      <div className="space-y-2">
        {PHONE_PRODUCTS.map(([name, price]) => (
          <div key={name} className="flex items-center justify-between rounded-xl border border-neutral-100 bg-[#FAFAFA] px-3 py-2.5">
            <div>
              <p className="text-[11px] font-semibold text-neutral-800 leading-tight">{name}</p>
              <p className="text-[10px] text-neutral-400 leading-tight">{price}</p>
            </div>
            <span className="mz-gold-fill mz-h text-[10px] font-bold text-[#1a1206] rounded-full px-3 py-1">Buy</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-[9.5px] font-medium text-neutral-300 tracking-wide">goldkey.studio/store/mia</p>
    </div>
  );
}

export const MzHero = ({ url, user, annual }) => (
  <section className="pt-32 sm:pt-36 pb-16 sm:pb-20">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#FDF8EA] px-3.5 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            <span className="mz-eyebrow !text-[10px]">GoldKey Monetization Hub</span>
          </span>
          <h1 data-testid="hero-headline" className="mz-h text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-[4.25rem] font-bold tracking-tighter">
            Turn your audience
            <br />
            <span className="mz-gold-text">into income.</span>
          </h1>
          <p className="mt-6 text-[15px] sm:text-base text-neutral-500 leading-relaxed max-w-md">
            Your storefront, products, checkout and analytics in one link. Built
            for personal brands that sell while they sleep.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <a href={url} data-testid="monetize-hero-cta" className="mz-btn-gold">
              <Sparkles size={14} strokeWidth={2} />
              Enable the Hub · {annual ? "$39/mo" : "$49/mo"}
              <ArrowUpRight size={14} strokeWidth={2.2} />
            </a>
            <Link to={user ? "/dashboard/monetize" : "/signup"} data-testid="monetize-hero-preview" className="mz-btn-ghost">
              {user ? "Open your Hub" : "Preview inside Studio"}
            </Link>
          </div>
          <p className="mt-5 text-[12px] text-neutral-400">
            No setup fees · Cancel anytime · Instant access after checkout
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="relative h-[460px] sm:h-[540px]"
          data-testid="hero-visual"
        >
          <div className="absolute right-0 top-2 w-[74%] sm:w-[66%] h-[82%] rounded-3xl overflow-hidden rotate-2 shadow-[0_30px_80px_rgba(10,10,10,0.18)]">
            <img src={HERO_IMG} alt="A creator filming social media content that sells through their storefront." className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="absolute left-0 bottom-2 w-[56%] max-w-[240px] z-10">
            <PhoneMockup />
          </div>
          <div className="absolute top-0 right-[2%] sm:right-[6%] z-20 mz-float">
            <div className="bg-white rounded-2xl shadow-[0_16px_40px_rgba(10,10,10,0.12)] border border-neutral-100 px-4 py-3">
              <p className="text-[10px] font-medium text-neutral-400 mb-0.5">Followers this week</p>
              <p className="mz-h text-sm font-bold flex items-center gap-1.5">
                +2,341
                <TrendingUp size={13} className="text-[#AA771C]" strokeWidth={2.4} />
              </p>
            </div>
          </div>
          <div className="absolute bottom-8 right-0 sm:right-[4%] z-20 mz-float-delay">
            <SaleToast />
          </div>
        </motion.div>
      </div>

      <div className="mt-16 sm:mt-24 grid grid-cols-2 md:grid-cols-4 border-y border-neutral-100" data-testid="hero-stats">
        {STATS.map(([v, l]) => (
          <div key={l} className="py-6 sm:py-8 text-center">
            <p className="mz-h text-2xl sm:text-3xl font-bold mz-gold-text">{v}</p>
            <p className="mt-1 text-[11px] sm:text-xs text-neutral-400 font-medium">{l}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
